// apps/web/src/pages/settings/opening-hours.tsx — Phase 2 plan 02-04 B6·B opening hours detail.
//
// Renders the per-weekday opening hours table + a "+ Manuelle Ausnahme"
// button that links to the day-exception modal. Below the weekday list:
// existing day exceptions.
//
// Per I18N-04 the preview row contains the literal "Europe/Berlin" string.
// Per D-11 wall-clock times are stored and rendered verbatim — no client-
// side timezone reinterpretation.
//
// AVL-01: every weekday has an explicit "Ruhetag" toggle (the missing
// `OpeningHour` row in the DB is the contract for "closed weekday", see
// D-09). The toggle flips a row between open (editable HH:mm) and closed
// (disabled inputs + visible "Geschlossen (Ruhetag)" tag). Save submits
// ONLY open days; closed days are omitted from the mutation payload so
// the server deletes them.

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Pencil, Trash2 } from "lucide-react";
import { gqlRequest } from "../../lib/api";
import {
  DAY_EXCEPTIONS_QUERY,
  DELETE_DAY_EXCEPTION_MUTATION,
  OPENING_HOURS_QUERY,
  UPSERT_OPENING_HOURS_MUTATION,
} from "./operations";

type Weekday =
  | "MONDAY"
  | "TUESDAY"
  | "WEDNESDAY"
  | "THURSDAY"
  | "FRIDAY"
  | "SATURDAY"
  | "SUNDAY";

interface OpeningHour {
  id: string;
  weekday: Weekday;
  openTime: string;
  closeTime: string;
  closeNextDay: boolean;
}

interface OpeningHoursResponse {
  openingHours: OpeningHour[];
}

interface DayException {
  id: string;
  date: string;
  status: "CLOSED" | "CUSTOM_RANGE";
  openTime: string | null;
  closeTime: string | null;
  closeNextDay: boolean | null;
}

interface DayExceptionsResponse {
  dayExceptions: DayException[];
}

// Per AVL-01: when a closed weekday is reopened it falls back to these
// defaults so the first-open inputs are sensible without a server round-
// trip. 11:00–22:00 matches the seed opening hours in the API tests.
const REOPEN_DEFAULTS = {
  openTime: "11:00",
  closeTime: "22:00",
  closeNextDay: false,
} as const;

// Per-row edit state. Each weekday owns its own tuple so closing preserves
// the entered times for reopening (D-09: the DB row only exists for open
// days, but the UI must remember what the admin typed).
interface WeekdayEditState {
  isClosed: boolean;
  openTime: string;
  closeTime: string;
  closeNextDay: boolean;
}

const WEEKDAY_KEYS: Weekday[] = [
  "MONDAY",
  "TUESDAY",
  "WEDNESDAY",
  "THURSDAY",
  "FRIDAY",
  "SATURDAY",
  "SUNDAY",
];

function buildInitialEditState(
  rows: OpeningHour[],
): Record<Weekday, WeekdayEditState> {
  const byWeekday = new Map<Weekday, OpeningHour>();
  for (const r of rows) byWeekday.set(r.weekday, r);
  const out = {} as Record<Weekday, WeekdayEditState>;
  for (const w of WEEKDAY_KEYS) {
    const api = byWeekday.get(w);
    if (api) {
      out[w] = {
        isClosed: false,
        openTime: api.openTime,
        closeTime: api.closeTime,
        closeNextDay: api.closeNextDay,
      };
    } else {
      out[w] = {
        isClosed: true,
        ...REOPEN_DEFAULTS,
      };
    }
  }
  return out;
}

// Merge API rows with per-weekday overrides. Used as the live edit
// state on every render so the user never sees an empty state in
// between the query resolving and an effect running.
function buildEditState(
  rows: OpeningHour[],
  overrides: Partial<Record<Weekday, WeekdayEditState>>,
): Record<Weekday, WeekdayEditState> {
  const base = buildInitialEditState(rows);
  const out = {} as Record<Weekday, WeekdayEditState>;
  for (const w of WEEKDAY_KEYS) {
    out[w] = overrides[w] ?? base[w];
  }
  return out;
}

// 260723-pqo: format a backend ISO date (`YYYY-MM-DD`) as `DD.MM.YYYY`.
// Pure string ops — never construct a Date — so a calendar day never
// drifts across a server timezone boundary. Falls back to the input on
// an unexpected shape so the UI degrades gracefully without throwing.
function formatGermanDate(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return iso;
  return `${m[3]}.${m[2]}.${m[1]}`;
}

export function OpeningHoursPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const hoursQuery = useQuery<OpeningHoursResponse>({
    queryKey: ["settings", "openingHours"],
    queryFn: async () => gqlRequest<OpeningHoursResponse>(OPENING_HOURS_QUERY),
  });

  const exceptionsQuery = useQuery<DayExceptionsResponse>({
    queryKey: ["settings", "dayExceptions"],
    queryFn: async () =>
      gqlRequest<DayExceptionsResponse>(DAY_EXCEPTIONS_QUERY),
  });

  // Controlled per-weekday edit state. We derive the displayed value
  // by overlaying any user overrides on top of the API rows. This
  // means rows render with the correct initial state on the very
  // first render after the query resolves (no effect-driven sync
  // dance that races with user clicks in tests).
  const apiRows = hoursQuery.data?.openingHours ?? [];
  const [overrides, setOverrides] = useState<
    Partial<Record<Weekday, WeekdayEditState>>
  >({});
  const editState: Record<Weekday, WeekdayEditState> | null = hoursQuery.data
    ? buildEditState(apiRows, overrides)
    : null;

  const upsertMutation = useMutation({
    mutationFn: async (
      rows: Array<{
        weekday: Weekday;
        openTime: string;
        closeTime: string;
        closeNextDay: boolean;
      }>,
    ) =>
      gqlRequest<OpeningHoursResponse>(UPSERT_OPENING_HOURS_MUTATION, {
        rows,
      }),
    onSuccess: () => {
      // Drop the user overrides — the server is now the source of
      // truth; the next render rebuilds editState from the fresh
      // query response.
      setOverrides({});
      void queryClient.invalidateQueries({
        queryKey: ["settings", "openingHours"],
      });
    },
  });

  // 260723-pqo: per-row delete. Confirms via window.confirm, fires the
  // mutation with the row's id, and invalidates the dayExceptions
  // query key so the list refetches. While the mutation is in-flight,
  // a local set of in-flight ids disables the matching Edit/Delete
  // controls to prevent double-fires (the server is idempotent, but a
  // double-click is still a UX bug).
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const deleteMutation = useMutation<
    { deleteDayException: boolean },
    Error,
    string
  >({
    mutationFn: async (id: string) =>
      gqlRequest<{ deleteDayException: boolean }>(
        DELETE_DAY_EXCEPTION_MUTATION,
        { id },
      ),
    onSettled: () => {
      setDeletingId(null);
      void queryClient.invalidateQueries({
        queryKey: ["settings", "dayExceptions"],
      });
    },
  });

  function handleEdit(ex: DayException) {
    // Per scope 3 of the plan: the Edit control pushes the row as
    // Location-State AND sets ?id= so the dialog can hydrate from the
    // single source of truth AND survive a manual reload.
    navigate(
      `/admin/einstellungen/oeffnungszeiten/ausnahme?id=${encodeURIComponent(ex.id)}`,
      { state: { dayException: ex } },
    );
  }

  function handleDelete(ex: DayException) {
    const ok = window.confirm(
      t("settings.hours.exceptionDeleteConfirm", {
        date: formatGermanDate(ex.date),
      }),
    );
    if (!ok) return;
    setDeletingId(ex.id);
    deleteMutation.mutate(ex.id);
  }

  function setRow(weekday: Weekday, patch: Partial<WeekdayEditState>) {
    setOverrides((prev) => {
      const baseRow = editState?.[weekday];
      if (!baseRow) return prev;
      return {
        ...prev,
        [weekday]: { ...baseRow, ...patch },
      };
    });
  }

  function toggleRuhetag(weekday: Weekday) {
    setOverrides((prev) => {
      const baseRow = editState?.[weekday];
      if (!baseRow) return prev;
      const next: WeekdayEditState = baseRow.isClosed
        ? { ...baseRow, isClosed: false }
        : { ...baseRow, isClosed: true };
      return { ...prev, [weekday]: next };
    });
  }

  const monday = editState?.MONDAY;
  const previewRange =
    monday && !monday.isClosed
      ? `${monday.openTime} – ${monday.closeTime}`
      : "—";

  return (
    <main className="min-h-screen bg-bg text-text font-body">
      <header className="sticky top-0 z-10 bg-bg border-b border-divider px-4 py-3 flex items-center gap-3">
        <Link
          to="/admin/einstellungen"
          className="text-text/70 hover:text-text"
          aria-label={t("settings.hub.backToHub")}
          data-testid="hours-back"
        >
          ‹
        </Link>
        <h1
          className="font-heading text-2xl md:text-3xl"
          data-testid="hours-title"
        >
          {t("settings.hours.title")}
        </h1>
      </header>

      <div className="px-4 py-6 w-full max-w-3xl space-y-6">
        {/* I18N-04 — the timezone label is rendered verbatim per plan.
            The Vitest asserts this exact substring is present in the DOM. */}
        <p className="text-sm text-text/70" data-testid="hours-tz-preview">
          {t("settings.hours.preview", { range: previewRange })}
        </p>

        <ul className="card divide-y divide-divider" data-testid="hours-list">
          {WEEKDAY_KEYS.map((weekday) => {
            const row = editState?.[weekday];
            return (
              <WeekdayRow
                key={weekday}
                weekday={weekday}
                row={row ?? null}
                onToggleRuhetag={() => toggleRuhetag(weekday)}
                onChange={(patch) => setRow(weekday, patch)}
                testId={`hours-row-${weekday.toLowerCase()}`}
              />
            );
          })}
        </ul>

        <div className="flex justify-end">
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => {
              if (!editState) return;
              const rows = WEEKDAY_KEYS.flatMap((weekday) => {
                const row = editState[weekday];
                if (row.isClosed) return [];
                return [
                  {
                    weekday,
                    openTime: row.openTime,
                    closeTime: row.closeTime,
                    closeNextDay: row.closeNextDay,
                  },
                ];
              });
              upsertMutation.mutate(rows);
            }}
            disabled={upsertMutation.isPending || editState === null}
            data-testid="hours-save"
          >
            {t("settings.restaurant.save")}
          </button>
        </div>

        <section>
          <div className="flex justify-end mb-2">
            <Link
              to="/admin/einstellungen/oeffnungszeiten/ausnahme"
              className="btn btn-secondary"
              data-testid="hours-add-exception"
            >
              {t("settings.hours.addException")}
            </Link>
          </div>

          {exceptionsQuery.isLoading && (
            <p className="text-sm text-text/70">…</p>
          )}

          {exceptionsQuery.data?.dayExceptions.length === 0 && (
            <p className="text-sm text-text/70" data-testid="hours-empty">
              {t("settings.hours.empty")}
            </p>
          )}

          {exceptionsQuery.data?.dayExceptions.length ? (
            <ul
              className="card divide-y divide-divider"
              data-testid="hours-exceptions"
            >
              {exceptionsQuery.data.dayExceptions.map((ex) => (
                <li
                  key={ex.id}
                  className="px-4 py-2 flex flex-wrap items-center justify-between gap-2"
                  data-testid={`hours-exception-${ex.date}`}
                >
                  <div className="flex flex-col">
                    <span className="font-medium">
                      {formatGermanDate(ex.date)}
                    </span>
                    {ex.status === "CUSTOM_RANGE" &&
                    ex.openTime &&
                    ex.closeTime ? (
                      <span className="text-sm text-text/70">
                        {t("settings.hours.exceptionTimeRange", {
                          openTime: ex.openTime,
                          closeTime: ex.closeTime,
                          nextDay: ex.closeNextDay === true ? " +1" : "",
                        })}
                      </span>
                    ) : null}
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="tag">
                      {ex.status === "CLOSED"
                        ? t("settings.hours.exceptionStatusClosed")
                        : t("settings.hours.exceptionStatusCustom")}
                    </span>
                    <button
                      type="button"
                      className="p-1 text-text/70 hover:text-text disabled:opacity-40 disabled:cursor-not-allowed"
                      onClick={() => handleEdit(ex)}
                      disabled={deletingId === ex.id}
                      aria-label={t("settings.hours.exceptionEdit")}
                      data-testid={`hours-exception-edit-${ex.id}`}
                    >
                      <Pencil className="h-4 w-4" aria-hidden="true" />
                    </button>
                    <button
                      type="button"
                      className="p-1 text-text/70 hover:text-red-700 disabled:opacity-40 disabled:cursor-not-allowed"
                      onClick={() => handleDelete(ex)}
                      disabled={deletingId === ex.id}
                      aria-label={t("settings.hours.exceptionDelete")}
                      data-testid={`hours-exception-delete-${ex.id}`}
                    >
                      <Trash2 className="h-4 w-4" aria-hidden="true" />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          ) : null}
        </section>
      </div>
    </main>
  );
}

function WeekdayRow({
  weekday,
  row,
  onToggleRuhetag,
  onChange,
  testId,
}: {
  weekday: Weekday;
  row: WeekdayEditState | null;
  onToggleRuhetag: () => void;
  onChange: (patch: Partial<WeekdayEditState>) => void;
  testId: string;
}) {
  const { t } = useTranslation();
  const labelKey = `settings.hours.weekday${weekday.charAt(0)}${weekday.charAt(1).toLowerCase()}`;
  const closed = !row || row.isClosed;
  return (
    <li
      className="px-4 py-3 flex flex-wrap items-center gap-3"
      data-testid={testId}
    >
      <span className="w-12 font-medium">{t(labelKey)}</span>
      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          role="switch"
          checked={row?.isClosed ?? true}
          onChange={onToggleRuhetag}
          aria-label={t("settings.hours.ruhetag")}
          data-testid={`${testId}-ruhetag`}
          className="h-4 w-4"
        />
        <span>{t("settings.hours.ruhetag")}</span>
      </label>
      <input
        type="time"
        value={row?.openTime ?? ""}
        disabled={closed}
        onChange={(e) => onChange({ openTime: e.target.value })}
        className="field"
        data-testid={`${testId}-open`}
      />
      <span aria-hidden="true">–</span>
      <input
        type="time"
        value={row?.closeTime ?? ""}
        disabled={closed}
        onChange={(e) => onChange({ closeTime: e.target.value })}
        className="field"
        data-testid={`${testId}-close`}
      />
      {closed && (
        <span className="tag" data-testid={`${testId}-closed`}>
          {t("settings.hours.closed")}
        </span>
      )}
    </li>
  );
}
